import React, {useMemo, useState} from 'react';
import {postData} from '../../Utils/Server';
import Endpoints from '../../Constants/Endpoints';
import {currentUser} from '../../Utils/Auth';
import {Notify, SUCCESS, ERROR} from '../../Utils/Notify';
import PageLayout from '../Layout/PageLayout';
import {Card, Button, FormField, TextInput, StatusBadge, Modal, tokens} from '../UI';

// Selectable resources — keys MUST match the backend resetResourceTables map.
// `cascades` is shown to the admin so they know exactly what each choice removes.
const RESOURCES = [
    {key: 'requests', label: 'Requests / deliveries', cascades: 'drop-offs, tracking, rider assignments, their invoices'},
    {key: 'invoices', label: 'Invoices', cascades: 'invoice records only'},
    {key: 'businesses', label: 'Businesses (corporate)', cascades: 'departments, billing accounts, documents, invoices'},
    {key: 'users', label: 'Users (customers)', cascades: 'ALL their requests, businesses, invoices, complaints, payments, ledger'},
    {key: 'riders', label: 'Riders', cascades: 'assignments, sessions, fuel loans'},
    {key: 'complaints', label: 'Complaints', cascades: 'complaint records only'},
    {key: 'transactions', label: 'Transactions / payments', cascades: 'gateway + manual payment records'},
    {key: 'ledger', label: 'Wallet ledger', cascades: 'wallet/rider ledger entries'},
    {key: 'promotions', label: 'Promotions', cascades: 'promo codes'},
    {key: 'broadcasts', label: 'Broadcasts', cascades: 'broadcasts, push notifications'},
    {key: 'config', label: 'Pricing & config', cascades: 'areas, regions, rates, request/means types, peak hours, surcharges, hubs'},
    {key: 'audit_logs', label: 'Audit logs', cascades: 'the admin audit trail'},
];

const CONFIRM_PHRASE = 'RESET';

export default function SystemReset() {
    const isSuperAdmin = (currentUser()?.role || '') === 'super_admin';

    const [selected, setSelected] = useState({});
    const [password, setPassword] = useState('');
    const [confirmation, setConfirmation] = useState('');
    const [showConfirm, setShowConfirm] = useState(false);
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);

    const chosen = useMemo(() => RESOURCES.filter((r) => selected[r.key]), [selected]);
    const canSubmit = chosen.length > 0 && password && confirmation === CONFIRM_PHRASE;

    if (!isSuperAdmin) {
        return (
            <PageLayout title="System reset">
                <Card>
                    <StatusBadge tone="danger">Restricted</StatusBadge>
                    <p style={{marginTop: tokens.spacing[3], color: tokens.colors.body}}>
                        Only a super admin can access the data reset tool.
                    </p>
                </Card>
            </PageLayout>
        );
    }

    const toggle = (key) => setSelected((s) => ({...s, [key]: !s[key]}));

    const runReset = async () => {
        setBusy(true);
        const fd = new FormData();
        fd.append('resources', chosen.map((r) => r.key).join(','));
        fd.append('password', password);
        fd.append('confirmation', confirmation);
        try {
            const res = await postData(Endpoints.SYSTEM_RESET, fd);
            const json = await res.json();
            if (json.success === 1) {
                setResult(json.data);
                Notify('Data cleared', `Removed ${json.data?.total_rows ?? 0} rows across ${json.data?.total_tables ?? 0} tables`, SUCCESS);
                setSelected({});
                setPassword('');
                setConfirmation('');
            } else {
                const msg = Array.isArray(json.message) ? json.message[0] : (json.message || json.error || 'Reset failed');
                Notify('Error', msg, ERROR);
            }
        } catch (e) {
            Notify('Error', 'Reset failed', ERROR);
        } finally {
            setBusy(false);
            setShowConfirm(false);
        }
    };

    return (
        <PageLayout title="System reset">
            <Card tone="brand" style={{marginBottom: tokens.spacing[4], borderColor: tokens.colors.status.danger.border}}>
                <StatusBadge tone="danger">Destructive</StatusBadge>
                <p style={{marginTop: tokens.spacing[3], color: tokens.colors.body, lineHeight: 1.6}}>
                    Permanently deletes the selected data from the database. Admin accounts and settings are never touched.
                    This cannot be undone — selecting a resource also clears everything that depends on it.
                </p>
            </Card>

            <Card style={{marginBottom: tokens.spacing[4]}}>
                <h6 style={{fontWeight: 700, marginBottom: tokens.spacing[3]}}>Choose what to clear</h6>
                <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: tokens.spacing[3]}}>
                    {RESOURCES.map((r) => (
                        <label
                            key={r.key}
                            style={{
                                display: 'flex', gap: tokens.spacing[3], alignItems: 'flex-start',
                                padding: tokens.spacing[3], borderRadius: tokens.radii.md,
                                border: `1px solid ${selected[r.key] ? tokens.colors.status.danger.border : tokens.colors.border}`,
                                background: selected[r.key] ? tokens.colors.status.danger.bg : tokens.colors.surface,
                                cursor: 'pointer',
                            }}
                        >
                            <input type="checkbox" checked={!!selected[r.key]} onChange={() => toggle(r.key)} style={{marginTop: 3}}/>
                            <span>
                                <span style={{fontWeight: 600, display: 'block'}}>{r.label}</span>
                                <span style={{fontSize: 12, color: tokens.colors.muted}}>Also clears: {r.cascades}</span>
                            </span>
                        </label>
                    ))}
                </div>
            </Card>

            <Card style={{marginBottom: tokens.spacing[4]}}>
                <h6 style={{fontWeight: 700, marginBottom: tokens.spacing[3]}}>Confirm it's you</h6>
                <div style={{display: 'flex', gap: tokens.spacing[4], flexWrap: 'wrap'}}>
                    <FormField label="Your password" htmlFor="resetPassword" style={{flex: '1 1 240px'}}>
                        <TextInput id="resetPassword" type="password" value={password}
                                   onChange={(e) => setPassword(e.target.value)} placeholder="Re-enter your password"/>
                    </FormField>
                    <FormField label={`Type ${CONFIRM_PHRASE} to confirm`} htmlFor="resetPhrase" style={{flex: '1 1 240px'}}>
                        <TextInput id="resetPhrase" value={confirmation}
                                   onChange={(e) => setConfirmation(e.target.value)} placeholder={CONFIRM_PHRASE}/>
                    </FormField>
                </div>
            </Card>

            <Button variant="danger" disabled={!canSubmit || busy} onClick={() => setShowConfirm(true)}>
                {busy ? 'Clearing…' : `Clear ${chosen.length} resource${chosen.length === 1 ? '' : 's'}`}
            </Button>

            {result && (
                <Card style={{marginTop: tokens.spacing[4]}}>
                    <h6 style={{fontWeight: 700, marginBottom: tokens.spacing[2]}}>
                        Cleared {result.total_rows} rows across {result.total_tables} tables
                    </h6>
                    <ul style={{margin: 0, paddingLeft: tokens.spacing[4], color: tokens.colors.body}}>
                        {Object.entries(result.cleared || {}).map(([t, c]) => (
                            <li key={t}>{t}: {c}</li>
                        ))}
                    </ul>
                </Card>
            )}

            <Modal
                open={showConfirm}
                onClose={() => setShowConfirm(false)}
                title="Final confirmation"
                subtitle="This permanently deletes the selected data. There is no undo."
                actions={
                    <>
                        <Button variant="ghost" onClick={() => setShowConfirm(false)}>Cancel</Button>
                        <Button variant="danger" disabled={busy} onClick={runReset}>
                            {busy ? 'Clearing…' : 'Yes, clear it'}
                        </Button>
                    </>
                }
            >
                <p style={{color: tokens.colors.body, lineHeight: 1.6}}>You're about to clear:</p>
                <ul style={{paddingLeft: tokens.spacing[4], color: tokens.colors.body}}>
                    {chosen.map((r) => (
                        <li key={r.key}><strong>{r.label}</strong> — {r.cascades}</li>
                    ))}
                </ul>
            </Modal>
        </PageLayout>
    );
}
